import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, UserPlus, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommitteeJoinPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, setAuthToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchInviteInfo();
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const response = await axios.get(`${API}/committee-invite/${token}`);
      setInviteInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    
    if (!inviteInfo.existing_user) {
      if (!password) {
        toast.error('Please enter a password');
        return;
      }
      if (password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    setSubmitting(true);
    try {
      const headers = {};
      if (inviteInfo.existing_user && user) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
      }

      const response = await axios.post(
        `${API}/committee-invite/${token}/accept`,
        null,
        {
          params: inviteInfo.existing_user ? {} : { password },
          headers
        }
      );

      // If new user, log them in
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        // Set the auth token and user in context
        if (setAuthToken) {
          setAuthToken(response.data.access_token, response.data.user);
        }
      }

      toast.success(response.data.message);
      
      // Redirect to committee dashboard
      setTimeout(() => {
        navigate('/committee-dashboard');
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleDisplay = (role) => {
    const roles = {
      'chair': 'Chair',
      'co_chair': 'Co-Chair',
      'member': 'Committee Member'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-greek-blue to-blue-900">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-greek-blue to-blue-900 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Invitation</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-greek-blue hover:bg-blue-700">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-greek-blue to-blue-900 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
              alt="AHEPA Logo" 
              className="w-12 h-12 object-contain rounded-full"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
              alt="DOP Logo" 
              className="w-12 h-12 object-contain rounded-full"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Committee Invitation</h1>
          <p className="text-slate-600">You've been invited to join a scholarship committee</p>
        </div>

        <div className="bg-slate-50 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-greek-blue/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-greek-blue" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{inviteInfo.committee_name}</h2>
              <p className="text-sm text-slate-500">Your role: <span className="font-medium text-greek-blue">{getRoleDisplay(inviteInfo.role)}</span></p>
            </div>
          </div>
          
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Invited by:</span> {inviteInfo.invited_by_name || 'Administrator'}
            </p>
            <p className="text-sm text-slate-600 mt-1">
              <span className="font-medium">Your email:</span> {inviteInfo.email}
            </p>
          </div>
        </div>

        <form onSubmit={handleAccept}>
          {!inviteInfo.existing_user && (
            <div className="space-y-4 mb-6">
              <p className="text-sm text-slate-600 text-center">Create your account to get started</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">First Name</Label>
                  <Input value={inviteInfo.first_name} disabled className="bg-slate-50" />
                </div>
                <div>
                  <Label className="text-slate-700">Last Name</Label>
                  <Input value={inviteInfo.last_name} disabled className="bg-slate-50" />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  data-testid="password-input"
                />
              </div>
              
              <div>
                <Label className="text-slate-700">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
          )}

          {inviteInfo.existing_user && !user && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800 mb-3">
                <strong>Account Found:</strong> An account already exists with the email <strong>{inviteInfo.email}</strong>.
              </p>
              <p className="text-sm text-amber-800 mb-4">
                Please log in with your existing account to accept this invitation.
              </p>
              <Button
                type="button"
                onClick={() => {
                  // Store the invite token so we can redirect back after login
                  sessionStorage.setItem('pendingCommitteeInvite', token);
                  navigate('/login');
                }}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                Log In to Accept Invitation
              </Button>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-greek-blue hover:bg-blue-700"
            disabled={submitting || (inviteInfo.existing_user && !user)}
            data-testid="accept-invite-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Accept & Join Committee
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CommitteeJoinPage;
